/**
 * Report analytics — the presentational pieces.
 *
 * Stat tiles, chart cards, ranked bar lists and a part-to-whole meter. Every
 * report's analytics view is assembled from these, so the two reports (and the
 * next one) look like one product rather than two dashboards.
 *
 * Two rules hold across all of them:
 *   - text wears text tokens, never a series color; identity comes from a
 *     colored mark BESIDE the label (a dot, a bar), never from tinting words;
 *   - values are directly labelled here, which is what licenses the lighter
 *     hues in the palette to be used at all.
 */
import type { JSX, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { SEQUENTIAL, VIZ, hourLabel, WEEKDAYS } from './viz.js'

const CARD = 'rounded-[3px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]'

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function AnalyticsCanvas({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-full bg-[#F7F9FC] px-5 py-5">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-5">{children}</div>
    </div>
  )
}

/**
 * Section heading with a hairline rule running to the right edge — the cheap,
 * quiet way to separate bands of cards without boxing them.
 */
export function SectionLabel({
  children,
  note,
}: {
  children: ReactNode
  note?: string
}): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <h2 className="shrink-0 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
        {children}
      </h2>
      {note !== undefined && (
        <span className="shrink-0 text-[11px] text-slate-400">{note}</span>
      )}
      <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
    </div>
  )
}

/** Auto-fitting tile row: tiles keep a sane width instead of stretching thin. */
export function KpiRow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(184px,1fr))]">
      {children}
    </div>
  )
}

export function CardGrid({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid gap-5 xl:grid-cols-2">{children}</div>
}

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

export type Tone = 'default' | 'good' | 'warning' | 'critical'

const TONE_INK: Record<Tone, string> = {
  default: 'text-[#0f172a]',
  good: 'text-[#0B7A0B]',
  warning: 'text-[#A9740A]',
  critical: 'text-[#B33131]',
}

const TONE_DOT: Record<Tone, string> = {
  default: 'bg-slate-300',
  good: 'bg-[#0CA30C]',
  warning: 'bg-[#FAB219]',
  critical: 'bg-[#D03B3B]',
}

/**
 * `label · value · hint`, with an optional sparkline behind the value.
 *
 * The value uses proportional figures on purpose — tabular-nums gives every
 * digit a `0`'s width, which reads loose at this size. A tone shows as a dot
 * beside the label as well as tinting the number, so severity is never carried
 * by color alone.
 */
export function StatTile({
  label,
  value,
  hint,
  tone = 'default',
  spark,
}: {
  label: string
  value: string
  hint?: string
  tone?: Tone
  /** 8–24 points; drawn as a bare trend behind the value. */
  spark?: readonly number[]
}): JSX.Element {
  return (
    <div className={`${CARD} relative overflow-hidden px-4 py-3.5`}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
        {tone !== 'default' && (
          <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]}`} aria-hidden="true" />
        )}
        <span className="truncate">{label}</span>
      </p>
      <p className={`mt-1.5 text-[26px] font-semibold leading-none tracking-tight ${TONE_INK[tone]}`}>
        {value}
      </p>
      {hint !== undefined && (
        <p className="mt-1.5 truncate text-[11.5px] leading-tight text-slate-400">{hint}</p>
      )}
      {spark !== undefined && spark.length > 1 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 opacity-70">
          <Sparkline values={spark} />
        </div>
      )}
    </div>
  )
}

/**
 * Bare trend line — no axes, no labels, no tooltip. It answers "which way is
 * this going", and the chart card below answers everything else.
 */
export function Sparkline({
  values,
  color = VIZ.series[0],
}: {
  values: readonly number[]
  color?: string
}): JSX.Element {
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const step = 100 / Math.max(values.length - 1, 1)
  const points = values.map((v, i) => `${i * step},${32 - ((v - min) / span) * 28}`)
  const line = points.join(' ')
  const area = `0,32 ${line} 100,32`

  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="h-full w-full" aria-hidden="true">
      <polygon points={area} fill={color} opacity={0.08} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/**
 * The band a view leads with: one hero figure, a supporting line of facts, and
 * an optional trend. Exactly one per view — a second hero is just a KPI row.
 */
export function HeroBand({
  eyebrow,
  value,
  caption,
  facts,
  spark,
  Icon,
}: {
  eyebrow: string
  value: string
  caption: string
  facts: { label: string; value: string; tone?: Tone }[]
  spark?: readonly number[]
  Icon?: LucideIcon
}): JSX.Element {
  return (
    <section className={`${CARD} relative overflow-hidden`}>
      <div className="flex flex-col gap-6 p-5 lg:flex-row lg:items-center lg:gap-8">
        <div className="flex items-center gap-4">
          {Icon !== undefined && (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              {eyebrow}
            </p>
            {/* Hero figure: the one number the view leads with. */}
            <p className="mt-1 text-[44px] font-semibold leading-none tracking-tight text-[#0f172a]">
              {value}
            </p>
            <p className="mt-1.5 text-[12px] text-slate-500">{caption}</p>
          </div>
        </div>

        {spark !== undefined && spark.length > 1 && (
          <div className="h-12 min-w-0 flex-1 lg:max-w-[340px]">
            <Sparkline values={spark} />
          </div>
        )}

        <dl className="grid flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:justify-items-end">
          {facts.map((fact) => (
            <div key={fact.label} className="min-w-0">
              <dt className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
                {fact.label}
              </dt>
              <dd
                className={`mt-1 text-[17px] font-semibold leading-none ${TONE_INK[fact.tone ?? 'default']}`}
              >
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Chart card
// ---------------------------------------------------------------------------

export function ChartCard({
  title,
  subtitle,
  children,
  height = 320,
  wide = false,
  Icon,
  badge,
  footnote,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  height?: number
  /** Span both columns of CardGrid — for time series and long rankings. */
  wide?: boolean
  Icon?: LucideIcon
  /** Short right-aligned fact — a scope note, a total, a period. */
  badge?: string
  /** One line under the plot: the finding, in words. */
  footnote?: ReactNode
}): JSX.Element {
  return (
    <section className={`${CARD} flex flex-col ${wide ? 'xl:col-span-2' : ''}`}>
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 bg-[#FCFDFE] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {Icon !== undefined && (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
              <Icon className="h-3.5 w-3.5" />
            </span>
          )}
          <div className="min-w-0">
            <h3 className="truncate text-[13px] font-semibold text-[#0f172a]">{title}</h3>
            {subtitle !== undefined && (
              <p className="mt-0.5 truncate text-[11.5px] text-slate-400">{subtitle}</p>
            )}
          </div>
        </div>
        {badge !== undefined && (
          <span className="shrink-0 rounded-[3px] bg-slate-100 px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-slate-500">
            {badge}
          </span>
        )}
      </header>
      {/*
        NOT `flex-1`.

        In an auto-height column flex container, `flex: 1 1 0%` makes the item's
        hypothetical main size 0, and with no free space to distribute it stays
        0. HTML children (bar lists, meters) simply overflowed a zero-height box
        and still looked right, which is why this hid so well — but a <canvas>
        has no intrinsic height, so Chart.js measured a 0px parent and drew
        nothing. A definite height on the plot box is what every canvas chart
        here sizes itself against.
      */}
      <div className="min-w-0 overflow-hidden p-4" style={{ height }}>
        {children}
      </div>
      {footnote !== undefined && (
        <footer className="border-t border-slate-100 px-4 py-2.5 text-[11.5px] leading-snug text-slate-500">
          {footnote}
        </footer>
      )}
    </section>
  )
}

/** Shown inside a card when the dimension has no values in the current result set. */
export function CardEmpty({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center px-4 text-center text-[12px] text-slate-400">
      {message}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ranked bar list
// ---------------------------------------------------------------------------

export interface BarListItem {
  label: string
  value: number
  /** Secondary fact for the row — a rate, a share, a duration. */
  meta?: string
}

/**
 * A ranked list where the bar is the row's background rather than a chart.
 *
 * Chosen over a canvas bar chart for category rankings because every row is
 * directly labelled with its name AND its value: no legend, no axis lookup,
 * no color needed to tell rows apart. One hue for every bar — length already
 * encodes magnitude, so tinting by rank would double-encode it.
 */
export function BarList({
  items,
  format,
  emptyMessage = 'Nothing to rank here.',
  color = VIZ.series[0],
}: {
  items: readonly BarListItem[]
  format: (value: number) => string
  emptyMessage?: string
  color?: string
}): JSX.Element {
  if (items.length === 0) return <CardEmpty message={emptyMessage} />
  const max = Math.max(...items.map((i) => i.value), 1)

  return (
    <ol className="scrollbar-thin flex h-full flex-col gap-2.5 overflow-y-auto pr-1">
      {items.map((item) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-[12.5px] font-medium text-[#0f172a]" title={item.label}>
              {item.label}
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              {item.meta !== undefined && (
                <span className="text-[11px] text-slate-400">{item.meta}</span>
              )}
              <span className="text-[12.5px] font-semibold tabular-nums text-[#0f172a]">
                {format(item.value)}
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-[6px] w-full rounded-[3px]" style={{ background: VIZ.track }}>
            {/* Square at the baseline, 4px rounded at the growing end. */}
            <div
              className="h-full rounded-r-[4px]"
              style={{
                width: `${Math.max((item.value / max) * 100, item.value > 0 ? 2 : 0)}%`,
                background: color,
              }}
            />
          </div>
        </li>
      ))}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Leaderboard
// ---------------------------------------------------------------------------

export interface RankColumn<T> {
  label: string
  render: (row: T) => ReactNode
  align?: 'left' | 'right'
}

/**
 * A ranked table with an inline magnitude bar behind the leading column.
 *
 * This is the form for "more than ~7 classes that all carry meaning": a chart
 * would need a color per row and still be less precise than the numbers. The
 * bar keeps the at-a-glance comparison a chart would have given.
 */
export function RankTable<T>({
  rows,
  keyOf,
  nameOf,
  shareOf,
  columns,
  emptyMessage = 'Nothing to rank here.',
}: {
  rows: readonly T[]
  keyOf: (row: T) => string
  nameOf: (row: T) => string
  /** 0–1: how long the row's background bar runs. */
  shareOf: (row: T) => number
  columns: RankColumn<T>[]
  emptyMessage?: string
}): JSX.Element {
  if (rows.length === 0) return <CardEmpty message={emptyMessage} />

  return (
    <div className="scrollbar-thin h-full overflow-auto">
      <table className="w-full border-separate border-spacing-0 text-[12.5px]">
        <thead>
          <tr>
            <th className="sticky top-0 z-10 bg-white pb-2 text-left text-[10.5px] font-bold uppercase tracking-[0.06em] text-slate-400">
              #
            </th>
            <th className="sticky top-0 z-10 bg-white pb-2 text-left text-[10.5px] font-bold uppercase tracking-[0.06em] text-slate-400">
              Agent
            </th>
            {columns.map((col) => (
              <th
                key={col.label}
                className={`sticky top-0 z-10 whitespace-nowrap bg-white pb-2 pl-3 text-[10.5px] font-bold uppercase tracking-[0.06em] text-slate-400 ${
                  col.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={keyOf(row)} className="group">
              <td className="relative border-t border-slate-100 py-2 pr-2 align-middle">
                {/* The bar is the row's own background, so it never competes
                    with the numbers for space. */}
                <span
                  className="absolute inset-y-[3px] left-0 rounded-r-[4px]"
                  style={{
                    width: `${Math.max(shareOf(row) * 100, 1.5)}%`,
                    background: VIZ.track,
                  }}
                  aria-hidden="true"
                />
                <span className="relative text-[11px] font-bold tabular-nums text-slate-400">
                  {index + 1}
                </span>
              </td>
              <td className="relative max-w-[220px] border-t border-slate-100 py-2 align-middle">
                <span className="relative block truncate font-medium text-[#0f172a]" title={nameOf(row)}>
                  {nameOf(row)}
                </span>
              </td>
              {columns.map((col) => (
                <td
                  key={col.label}
                  className={`relative whitespace-nowrap border-t border-slate-100 py-2 pl-3 align-middle tabular-nums text-slate-600 ${
                    col.align === 'right' ? 'text-right' : ''
                  }`}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Attention list
// ---------------------------------------------------------------------------

export interface AttentionItem {
  id: string
  title: string
  detail: string
  value: string
  tone: Exclude<Tone, 'default'>
}

/**
 * The panel that says what to *do*: rows that failed a threshold, worst first.
 *
 * Each row states its reason in words and carries an icon-weight dot, so the
 * severity is legible without relying on the color being distinguishable.
 */
export function AttentionList({
  items,
  emptyMessage,
}: {
  items: readonly AttentionItem[]
  emptyMessage: string
}): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ECF8EC]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#0CA30C]" aria-hidden="true" />
        </span>
        <p className="text-[12.5px] font-medium text-[#0f172a]">All clear</p>
        <p className="max-w-xs text-[11.5px] text-slate-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <ul className="scrollbar-thin flex h-full flex-col gap-2 overflow-y-auto pr-1">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-center gap-3 rounded-[3px] border border-slate-100 bg-[#FCFDFE] px-3 py-2.5"
        >
          <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[item.tone]}`} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-medium text-[#0f172a]">{item.title}</p>
            <p className="truncate text-[11px] text-slate-400">{item.detail}</p>
          </div>
          <span className={`shrink-0 text-[13px] font-semibold tabular-nums ${TONE_INK[item.tone]}`}>
            {item.value}
          </span>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

/**
 * Weekday × hour grid on a single-hue sequential ramp.
 *
 * Sequential, not categorical: the cell value is a magnitude, and one hue
 * light→dark is the only encoding that reads without a legend lookup. Empty
 * cells are the palest step rather than white, so "no volume" still reads as
 * part of the grid.
 */
/** 24 equal columns — shared by the cell rows and the tick row so they align. */
const HOUR_GRID = { gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' } as const

export function Heatmap({
  matrix,
  max,
  emptyMessage = 'No timestamps to plot.',
}: {
  matrix: readonly (readonly number[])[]
  max: number
  emptyMessage?: string
}): JSX.Element {
  if (max <= 0) return <CardEmpty message={emptyMessage} />

  const stepFor = (value: number): string => {
    if (value === 0) return SEQUENTIAL[0]
    // Six live steps above zero; the ramp is ordered, so a darker cell is
    // always a busier hour.
    const index = Math.min(SEQUENTIAL.length - 1, 1 + Math.floor((value / max) * (SEQUENTIAL.length - 2)))
    return SEQUENTIAL[index] ?? SEQUENTIAL[1]
  }

  return (
    <div className="flex h-full flex-col justify-between gap-3">
      <div className="min-w-0 overflow-x-auto">
        <div className="min-w-[520px]">
          {matrix.map((row, day) => (
            <div key={WEEKDAYS[day]} className="mb-[3px] flex items-center gap-2">
              <span className="w-8 shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                {WEEKDAYS[day]}
              </span>
              <div className="grid flex-1 gap-[3px]" style={HOUR_GRID}>
                {row.map((value, hour) => (
                  <div
                    key={hour}
                    className="h-5 rounded-[2px]"
                    style={{ background: stepFor(value) }}
                    title={`${WEEKDAYS[day]} ${hourLabel(hour)} — ${value} conversation${value === 1 ? '' : 's'}`}
                  />
                ))}
              </div>
            </div>
          ))}
          {/* Tick row mirrors the cell grid exactly, so a label sits under the
              column it names rather than near it. */}
          <div className="mt-1 flex items-center gap-2">
            <span className="w-8 shrink-0" aria-hidden="true" />
            <div className="grid flex-1 gap-[3px]" style={HOUR_GRID}>
              {Array.from({ length: 24 }, (_, hour) => (
                <span key={hour} className="whitespace-nowrap text-[10px] text-slate-400">
                  {hour % 6 === 0 ? hourLabel(hour) : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scale legend — required for a sequential encoding. */}
      <div className="flex items-center justify-end gap-2">
        <span className="text-[10.5px] text-slate-400">Quieter</span>
        <div className="flex gap-[2px]">
          {SEQUENTIAL.map((step) => (
            <span key={step} className="h-2.5 w-5 rounded-[2px]" style={{ background: step }} />
          ))}
        </div>
        <span className="text-[10.5px] text-slate-400">Busier · peak {max}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Part-to-whole meter
// ---------------------------------------------------------------------------

export interface MeterSegment {
  label: string
  value: number
  color: string
}

/**
 * Two or three mutually exclusive states as one bar — the honest form for a
 * part-to-whole that a donut would render harder to read.
 *
 * Segments are separated by a 2px gap in the surface color, and each carries a
 * legend row with a colored dot, its count and its share, so the split is
 * never conveyed by color alone.
 */
export function Meter({
  segments,
  emptyMessage = 'No values to split.',
}: {
  segments: readonly MeterSegment[]
  emptyMessage?: string
}): JSX.Element {
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  if (total <= 0) return <CardEmpty message={emptyMessage} />

  return (
    <div className="flex h-full flex-col justify-center gap-5">
      <div className="flex h-3 w-full gap-[2px] overflow-hidden rounded-[3px] bg-slate-100">
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <div
              key={s.label}
              className="h-full first:rounded-l-[3px] last:rounded-r-[3px]"
              style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
              title={`${s.label}: ${s.value}`}
            />
          ))}
      </div>
      <ul className="flex flex-col gap-2.5">
        {segments.map((s) => (
          <li key={s.label} className="flex items-center gap-2.5">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: s.color }}
              aria-hidden="true"
            />
            <span className="truncate text-[12.5px] font-medium text-[#0f172a]">{s.label}</span>
            <span className="ml-auto shrink-0 text-[12.5px] font-semibold tabular-nums text-[#0f172a]">
              {s.value.toLocaleString()}
            </span>
            <span className="w-14 shrink-0 text-right text-[11.5px] tabular-nums text-slate-400">
              {((s.value / total) * 100).toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
